import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface UIState {
  // Global UI state
  loading: boolean;
  error: string | null;

  // UI-specific state
  copySuccess: string | null;
  isCopying: boolean;
  lastClickTime: number;

  // Actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setCopySuccess: (message: string | null) => void;
  setIsCopying: (copying: boolean) => void;
  updateLastClickTime: () => void;

  // Computed values
  isRateLimited: boolean;

  // UI actions
  copyToClipboard: (text: string, label?: string) => Promise<void>;
  clearError: () => void;
  resetUI: () => void;
}

export const useUIStore = create<UIState>()(
  devtools(
    (set, get) => ({
      // Initial state
      loading: false,
      error: null,
      copySuccess: null,
      isCopying: false,
      lastClickTime: 0,

      // Actions
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),
      setCopySuccess: (message) => set({ copySuccess: message }),
      setIsCopying: (copying) => set({ isCopying: copying }),
      updateLastClickTime: () => set({ lastClickTime: Date.now() }),

      // Computed values
      get isRateLimited() {
        const { lastClickTime } = get();
        return Date.now() - lastClickTime < 1000;
      },

      // UI actions
      copyToClipboard: async (text, label = 'Text') => {
        const { isCopying, setCopySuccess } = get();

        if (isCopying) return;

        set({ isCopying: true, lastClickTime: Date.now() });

        try {
          await navigator.clipboard.writeText(text);
          set({ copySuccess: `${label} copied!` });

          // Clear success message after 2 seconds
          setTimeout(() => {
            set({ copySuccess: null });
          }, 2000);

        } catch (error) {
          console.error('Copy failed:', error);
          set({ error: 'Failed to copy to clipboard' });
        } finally {
          set({ isCopying: false });
        }
      },

      clearError: () => set({ error: null }),

      resetUI: () => set({
        loading: false,
        error: null,
        copySuccess: null,
        isCopying: false,
        lastClickTime: 0
      })
    }),
    { name: 'ui-store' }
  )
);
